import { faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FIELD_TYPES, FIELD_NAME_PATTERN, isReservedFieldName } from "./reservedIdentifiers";

// One row of a practice's CallerInformation schema: {name, type, required, pii_sensitivity}.
// Modeled directly on client/src/apiFunctions/AttributeField.js's repeatable-row pattern, but
// flat (no child attributes) since CallerInformation is a flat key/value bag, not nested JSON.
export const SchemaFieldEditor = ({ index, register, errors, removeField }) => {
  return (
    <div className="mb-2 border rounded p-2">
      <div className="d-flex justify-content-center align-items-center">
        <div className="col-5">
          <hr className="border border-secondary opacity-25 w-100" />
        </div>
        <div className="col-2 text-center">
          <span className="fs-6 fw-bold">Field {index + 1}</span>
        </div>
        <div className="col-5">
          <hr className="border border-secondary opacity-25 w-100" />
        </div>
      </div>

      <div className="d-flex align-items-center gap-2 mt-2">
        {/* Name */}
        <div className="col-3">
          <label className="form-label">
            <span className="text-danger">*</span> Name
          </label>
          <input
            type="text"
            placeholder="e.g. chart_id"
            className={`form-control px-2 ${errors?.fields?.[index]?.name ? "is-invalid" : ""}`}
            {...register(`fields.${index}.name`, {
              required: "Field name is required",
              validate: (value) => {
                const trimmed = String(value || "").trim();
                if (!FIELD_NAME_PATTERN.test(trimmed)) {
                  return "Lowercase letters, numbers, underscore only — must start with a letter (e.g. chart_id)";
                }
                if (isReservedFieldName(trimmed)) {
                  return "This name collides with a reserved column/tool name";
                }
                return true;
              },
            })}
            onKeyDown={(e) => e.key === " " && e.preventDefault()}
          />
          {errors?.fields?.[index]?.name && (
            <div className="invalid-feedback d-block">{errors.fields[index].name.message}</div>
          )}
        </div>

        {/* Type */}
        <div className="col-2">
          <label className="form-label">
            <span className="text-danger">*</span> Type
          </label>
          <select
            className={`form-select px-2 ${errors?.fields?.[index]?.type ? "is-invalid" : ""}`}
            {...register(`fields.${index}.type`, { required: true })}
          >
            <option value="">Select type</option>
            {FIELD_TYPES.map((type) => (
              <option key={type} value={type}>
                {type.charAt(0).toUpperCase() + type.slice(1)}
              </option>
            ))}
          </select>
        </div>

        {/* Required */}
        <div className="col-2">
          <label className="form-label">
            <span className="text-danger">*</span> Required
          </label>
          <select
            className={`form-select px-2 ${errors?.fields?.[index]?.required ? "is-invalid" : ""}`}
            {...register(`fields.${index}.required`, { required: true })}
          >
            <option value="">Select...</option>
            <option value="true">Yes</option>
            <option value="false">No</option>
          </select>
        </div>

        {/* PII sensitivity */}
        <div className="col-3">
          <label className="form-label">
            <span className="text-danger">*</span> PII Sensitivity
          </label>
          <select
            className={`form-select px-2 ${errors?.fields?.[index]?.pii_sensitivity ? "is-invalid" : ""}`}
            {...register(`fields.${index}.pii_sensitivity`, { required: true })}
          >
            <option value="">Select...</option>
            <option value="high">High (e.g. DOB, chart ID)</option>
            <option value="none">None (e.g. room number)</option>
          </select>
        </div>

        {/* Delete */}
        <div className="col-1">
          <span
            title="Delete field"
            className="ms-2 text-danger cursor-pointer fs-6 d-block mt-4"
            onClick={() => removeField(index)}
          >
            <FontAwesomeIcon icon={faTrash} />
          </span>
        </div>
      </div>
    </div>
  );
};
